import { type CompilerError, isUuid } from "./MiscUtils";

export interface ReleaseDocument {
  name: string;
  type: "ruledoc" | "rulesheet" | "datatable" | "mapping" | "connection" | "derivedtable";
  version: number;
  reference: string;
  locked?: {
    identity_id: string;
    full_name: string;
  };
  contents: any;
  errors?: CompilerError[];
  /** A temporary flag while the file is being deleted */
  deleted?: boolean;
}

// TODO this type is wrong, it should be above
export type ReleaseDocuments = Record<string, ReleaseDocument>;

export const findReleaseDocument = (
  documents: any,
  predicate: (document: ReleaseDocument, name: string) => boolean,
): ReleaseDocument | undefined => {
  for (const [key, document] of Object.entries(documents || {}) as any) {
    if (document?.reference) {
      if (predicate(document, key)) {
        return document;
      }
    }

    if (typeof document === "object") {
      const found = findReleaseDocument(document, predicate);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};

export const iterateReleaseDocuments = (
  documents: any,
  func: (document: ReleaseDocument, name: string) => void,
): void => {
  for (const [key, document] of Object.entries(documents || {}) as any) {
    if (document?.reference) {
      func(document, key);
    }

    if (typeof document === "object") {
      iterateReleaseDocuments(document, func);
    }
  }
};

export const iterateReleaseDocumentsAsync = async (
  documents: any,
  callback: (document: ReleaseDocument, name: string) => Promise<void>,
): Promise<void> => {
  for (const [key, document] of Object.entries(documents || {}) as any) {
    if (document?.reference) {
      await callback(document, key);
    }

    if (typeof document === "object") {
      await iterateReleaseDocumentsAsync(document, callback);
    }
  }
};

export const flattenReleaseDocuments = (documents: any): ReleaseDocument[] => {
  const releaseDocuments: ReleaseDocument[] = [];
  iterateReleaseDocuments(documents, (document, name) => {
    releaseDocuments.push(document);
  });
  return releaseDocuments;
};

export const trimDocName = (name: string) =>
  name
    .replace(/\.(docx|xlsx)$/i, "")
    .replace(/<[^>]*>/g, "") // remove html tags
    .trim();

export const sanitizeDocumentName = (name: string) => {
  if (typeof name !== "string") {
    return name;
  }

  let result = (name.startsWith("/") ? name : `/${name}`).trim();
  if (result.endsWith(".docx")) {
    result = result.split(".docx")[0];
  }
  if (result.endsWith(".xlsx")) {
    result = result.split(".xlsx")[0];
  }
  return result.replace(/\t/g, " ").trim();
};

export const isDocumentId = (id: string) => {
  if (!id) {
    return false;
  }
  return isUuid(id) || (id.length === 28 && id.startsWith("00"));
};
